/**
 * Janko Piano - Device Detection Module
 * Handles platform detection and device-specific configuration
 * @version 0.2.63
 */

import { CONFIG } from './config.js';
import { setQuickRows } from './ui.js';

// =============================================================================
// DEVICE DETECTION
// =============================================================================

export function autoDetectDevice(updateMinimapFn) {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
        const plt = window.Capacitor.getPlatform();
        CONFIG.detectedType = (plt === 'ios') ? 'ios' : 'android';
    } else {
        const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(ua);
        const isIpadOS = (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
        const hasFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        const isDesktop = (!isMobileUA && !isIpadOS) && (hasFinePointer || navigator.maxTouchPoints === 0);

        CONFIG.detectedType = isDesktop ? 'desktop' : 'mobile_web';
    }
    
    // Detect low-end devices for performance optimization
    detectLowEndDevice();

    // Set keyboard visuals based on device type
    if (CONFIG.detectedType === 'desktop') {
        CONFIG.keyboardVisuals = true;
        const btn = document.getElementById('btn-kb-layout');
        if (btn) btn.classList.add('active');
    } else {
        CONFIG.keyboardVisuals = false;
        const btn = document.getElementById('btn-kb-layout');
        if (btn) btn.classList.remove('active');
    }

    // Do not override user's saved rows.
    // Only clamp down if the device constraint lowered maxRows (e.g., small landscape).
    if (CONFIG.rows > CONFIG.maxRows) {
        setQuickRows(CONFIG.maxRows, updateMinimapFn);
    }

    // Update UI to reflect maxRows limit
    updateRowsUI();
}

// Update rows UI based on maxRows limit
function updateRowsUI() {
    // Update slider max
    const slider = document.getElementById('rows-slider');
    if (slider) {
        slider.max = CONFIG.maxRows;
    }
    
    // Hide buttons based on maxRows limit (maxRows=6 means hide both 8 and 12)
    const btn12 = document.getElementById('btn-qs-r12');
    const btn8 = document.getElementById('btn-qs-r8');
    
    console.log(`[updateRowsUI] maxRows=${CONFIG.maxRows}, hiding buttons: 12=${CONFIG.maxRows <= 6}, 8=${CONFIG.maxRows <= 6}`);
    
    if (btn12) {
        if (CONFIG.maxRows <= 6) {
            btn12.style.setProperty('display', 'none', 'important');
        } else {
            btn12.style.removeProperty('display');
        }
    }
    if (btn8) {
        if (CONFIG.maxRows <= 6) {
            btn8.style.setProperty('display', 'none', 'important');
        } else {
            btn8.style.removeProperty('display');
        }
    }
}

// Export for use after DOM is ready
export function applyMaxRowsLimit() {
    updateRowsUI();
}

// =============================================================================
// PLATFORM HELPERS
// =============================================================================

export function isNativePlatform() {
    return window.Capacitor && window.Capacitor.isNativePlatform();
}

export function getPlatformType() {
    if (isNativePlatform()) {
        return window.Capacitor.getPlatform();
    }
    return CONFIG.detectedType;
}

export function getCapacitorPlugin(name) {
    if (window.Capacitor && window.Capacitor.Plugins) {
        return window.Capacitor.Plugins[name];
    }
    return null;
}

// =============================================================================
// SCREEN SIZE DETECTION
// =============================================================================

function isSmallScreen() {
    // Detect small screen (phone-sized)
    // Use multiple signals: touch support, screen size, user agent
    const w = window.innerWidth;
    const h = window.innerHeight;
    const shorter = Math.min(w, h);
    
    // Check if touch device
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Check user agent for mobile
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Small screen = touch/mobile device with shorter dimension < 500px (phones)
    // 500px covers most phones up to ~6.7" (iPhone 15 Pro Max is 430px, Galaxy S24 Ultra is 412px)
    const isPhoneSized = (hasTouch || isMobileUA) && shorter < 500;
    
    console.log(`[Screen] ${w}x${h}, shorter: ${shorter}, touch: ${hasTouch}, mobileUA: ${isMobileUA}, isSmall: ${isPhoneSized}`);
    return isPhoneSized;
}

// =============================================================================
// LOW-END DEVICE DETECTION
// =============================================================================

function detectLowEndDevice() {
    // Heuristics for low-end device detection
    let score = 0;
    
    // Check hardware concurrency (CPU cores)
    const cores = navigator.hardwareConcurrency || 4;
    if (cores <= 2) score += 3;
    else if (cores <= 4) score += 1;
    
    // Check device memory (if available)
    const memory = navigator.deviceMemory || 4; // GB
    if (memory <= 2) score += 3;
    else if (memory <= 4) score += 1;
    
    // Check if mobile (mobile devices with low specs are more common)
    if (CONFIG.detectedType !== 'desktop') score += 1;
    
    // Check screen resolution (very high res on old device = slow)
    const pixels = window.screen.width * window.screen.height * (window.devicePixelRatio || 1);
    if (pixels > 4000000) score += 1; // More than ~4MP effective
    
    // Threshold: score > 4 is considered low-end
    CONFIG.isLowEnd = (score > 4);
    
    // Limit polyphony for low-end devices
    if (CONFIG.isLowEnd) {
        CONFIG.maxPolyphony = 16;
    } else {
        CONFIG.maxPolyphony = Infinity; // No limit for high-end devices
    }
    
    // Detect small screens (less than 7 inches) in landscape mode and limit rows to 6
    const w = window.innerWidth;
    const h = window.innerHeight;
    const diagonal = Math.sqrt(Math.pow(w, 2) + Math.pow(h, 2)) / (window.devicePixelRatio || 1);
    const isLandscape = w > h;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobileOrNative = CONFIG.detectedType !== 'desktop' || hasTouch || isMobileUA;
    
    // Less than 7-inch screen in landscape: diagonal < 900 CSS pixels, landscape mode
    const isSmallLandscape = isMobileOrNative && isLandscape && diagonal < 900;
    
    if (isSmallLandscape) {
        CONFIG.maxRows = 6;
        // Auto-switch to 6 rows if currently higher
        if (CONFIG.rows > 6) {
            CONFIG.rows = 6;
            if (typeof window.setQuickRows === 'function') {
                window.setQuickRows(6);
            }
        }
    } else {
        CONFIG.maxRows = 12;
    }
    
    // Update row buttons visibility using internal function
    updateRowsUI();
    
    console.log(`[Device] Screen: ${w}x${h}, diagonal: ${diagonal.toFixed(0)}, landscape: ${isLandscape}, mobileOrNative: ${isMobileOrNative}, smallLandscape: ${isSmallLandscape}`);
    
    console.log(`[Device] Low-end: ${CONFIG.isLowEnd}, maxDpr: ${CONFIG.maxDpr}, maxPolyphony: ${CONFIG.maxPolyphony}, maxRows: ${CONFIG.maxRows}, cores: ${cores}, memory: ${memory}GB`);
}
